import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import AppIcon from '../../components/AppIcon';
import TripTypeSelector from './components/TripTypeSelector';
import DestinationSelector from './components/DestinationSelector';
import DateSelector from './components/DateSelector';
import BudgetSelector from './components/BudgetSelector';
import AccommodationSelector from './components/AccommodationSelector';
import AdditionalDetails from './components/AdditionalDetails';
import ProgressIndicator from './components/ProgressIndicator';
import EnquirySuccessModal from './components/EnquirySuccessModal';

export default function CreateEnquiry() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedEnquiryId, setSubmittedEnquiryId] = useState(null);

  // Fetch user profile data on mount
  useEffect(() => {
    async function fetchUserProfile() {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('full_name, email, location')?.eq('id', user?.id)?.single();

        if (error) {
          console.error('Error fetching user profile:', error);
          return;
        }

        setUserProfile(data);
        
        // Pre-fill location if available
        if (data?.location) {
          setFormData(prev => ({ ...prev, customerLocation: data?.location }));
        }
      } catch (err) {
        console.error('Exception fetching user profile:', err);
      }
    }

    fetchUserProfile();
  }, [user?.id]);

  // Form data state
  const [formData, setFormData] = useState({
    tripType: '',
    destination: [],
    dateType: '',
    selectedDate: null,
    budget: {
      currency: 'GBP',
      symbol: '£',
      type: '',
      range: '',
      amount: null,
      min: null,
      max: null
    },
    accommodationStandard: '',
    adults: 1,
    children: 0,
    preferredNights: 7, // ✅ NEW: Default to 7 nights
    specialRequirements: '',
    moreDetails: '',
    customerLocation: '', // ✅ NEW: Customer location field
    travelPreferences: {
      activities: [],
      transport: [],
      dining: []
    }
  });

  const steps = [
    {
      id: 'trip-type',
      title: 'Trip Type',
      description: 'What kind of holiday are you planning?'
    },
    {
      id: 'destination',
      title: 'Destination',
      description: 'Where would you like to go?'
    },
    {
      id: 'dates',
      title: 'Dates',
      description: 'When do you want to travel?'
    },
    {
      id: 'budget',
      title: 'Budget',
      description: 'What\'s your travel budget?'
    },
    {
      id: 'details',
      title: 'Details',
      description: 'Tell us more about your preferences'
    }
  ];

  const totalSteps = steps?.length;

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.tripType) {
          newErrors.tripType = 'Please select a trip type';
        }
        break;
      case 2:
        if (!formData?.destination || formData?.destination?.length === 0) {
          newErrors.destination = 'Please select at least one destination country';
        }
        break;
      case 3:
        if (!formData?.dateType) {
          newErrors.dateType = 'Please select when you want to travel';
        } else if (formData?.dateType === 'specific' && (!formData?.selectedDate?.value?.startDate || !formData?.selectedDate?.value?.endDate)) {
          newErrors.dateType = 'Please select both departure and return dates';
        } else if (formData?.dateType === 'month') {
          // ✅ NEW: Validate month-only selection requires both month and year
          if (!formData?.selectedDate?.value?.month || !formData?.selectedDate?.value?.year) {
            newErrors.dateType = 'Please select both a month and year for travel';
          }
        } else if (formData?.dateType === 'season' && !formData?.selectedDate?.value) {
          newErrors.dateType = 'Please select your preferred season to travel';
        }
        break;
      case 4:
        // Updated validation for budget step to handle "no budget specified" option
        if (!formData?.budget?.type) {
          newErrors.budget = 'Please select a budget option';
        } else if (formData?.budget?.type !== 'no-budget') {
          // Only validate currency and amount if budget is being specified
          if (!formData?.budget?.currency) {
            newErrors.budget = 'Please select a currency';
          } else if (formData?.budget?.type === 'custom' && !formData?.budget?.amount) {
            newErrors.budget = 'Please enter your budget amount';
          }
        }
        if (!formData?.accommodationStandard) {
          newErrors.accommodation = 'Please select accommodation standard';
        }
        break;
      case 5:
        if (!formData?.adults || formData?.adults < 1) {
          newErrors.adults = 'At least 1 adult is required';
        }
        if (formData?.children < 0) {
          newErrors.children = 'Children count cannot be negative';
        }
        if (!formData?.customerLocation || formData?.customerLocation?.trim() === '') {
          newErrors.customerLocation = 'Please provide your location so agents know which airports you can depart from';
        }
        if (formData?.moreDetails && formData?.moreDetails?.length > 2000) {
          newErrors.moreDetails = 'More details must be 2000 characters or less';
        }
        
        // ✅ NEW: Validate preferred_nights
        if (!formData?.preferredNights || formData?.preferredNights < 1 || formData?.preferredNights > 60) {
          newErrors.preferredNights = 'Preferred nights must be between 1 and 60';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (!user?.id) {
        throw new Error('You must be logged in to create an enquiry');
      }

      if (!userProfile?.full_name) {
        throw new Error('User profile not loaded. Please refresh and try again.');
      }

      // Validate required fields
      if (!formData?.destination || formData?.destination?.length === 0 || !formData?.tripType) {
        throw new Error('Please complete all required fields');
      }

      if (!formData?.customerLocation || formData?.customerLocation?.trim() === '') {
        throw new Error('Please provide your location');
      }

      // Parse dates based on dateType
      let departureDate = null;
      let returnDate = null;
      let metadataDateInfo = {
        dateType: formData?.dateType
      };

      if (formData?.dateType === 'specific' && formData?.selectedDate?.value) {
        departureDate = formData?.selectedDate?.value?.startDate;
        returnDate = formData?.selectedDate?.value?.endDate;
        metadataDateInfo.specificDates = {
          startDate: departureDate,
          endDate: returnDate
        };
      } else if (formData?.dateType === 'month' && formData?.selectedDate?.value) {
        // For month-only, store month/year in metadata and set approximate dates
        const { month, year } = formData?.selectedDate?.value;
        metadataDateInfo.monthYear = { month, year };
        // Set first day of month as departure, last day as return
        departureDate = `${year}-${String(month)?.padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0)?.getDate();
        returnDate = `${year}-${String(month)?.padStart(2, '0')}-${lastDay}`;
      } else if (formData?.dateType === 'season' && formData?.selectedDate?.value) {
        metadataDateInfo.season = formData?.selectedDate?.value;
        // Leave dates null for season-only
      } else if (formData?.dateType === 'flexible') {
        metadataDateInfo.flexible = true;
        // Leave dates null for flexible
      }

      // Parse budget
      let budgetRange = null;
      let metadataBudgetInfo = {
        budgetType: formData?.budget?.type,
        currency: formData?.budget?.currency
      };

      if (formData?.budget?.type === 'custom' && formData?.budget?.amount) {
        budgetRange = JSON.stringify({
          min: 0,
          max: parseFloat(formData?.budget?.amount),
          currency: formData?.budget?.currency
        });
        metadataBudgetInfo.customAmount = parseFloat(formData?.budget?.amount);
      } else if (formData?.budget?.type === 'range' && formData?.budget?.min && formData?.budget?.max) {
        budgetRange = JSON.stringify({
          min: parseFloat(formData?.budget?.min),
          max: parseFloat(formData?.budget?.max),
          currency: formData?.budget?.currency
        });
        metadataBudgetInfo.range = {
          min: parseFloat(formData?.budget?.min),
          max: parseFloat(formData?.budget?.max)
        };
      } else if (formData?.budget?.type === 'no-budget') {
        metadataBudgetInfo.noBudgetSpecified = true;
      }

      // Create title
      const destinationStr = Array.isArray(formData?.destination) 
        ? formData?.destination?.join(', ')
        : formData?.destination;
      const title = `${formData?.tripType} to ${destinationStr}`;

      // Create description
      const description = `${formData?.tripType} trip to ${destinationStr}`;

      // Combine all metadata
      const metadata = {
        ...metadataDateInfo,
        ...metadataBudgetInfo,
        accommodationStandard: formData?.accommodationStandard,
        specialRequirements: formData?.specialRequirements,
        moreDetails: formData?.moreDetails,
        customerLocation: formData?.customerLocation,
        travelPreferences: formData?.travelPreferences,
        preferredNights: formData?.preferredNights // ✅ NEW: Store preferred_nights in metadata as well
      };

      // Create enquiry with customer name from profile
      const enquiryData = {
        customer_id: user?.id,
        customer_name: userProfile?.full_name,
        title,
        description,
        destination: destinationStr,
        trip_type: formData?.tripType,
        departure_date: departureDate,
        return_date: returnDate,
        adults: formData?.adults,
        children: formData?.children,
        preferred_nights: formData?.preferredNights, // ✅ NEW: Add preferred_nights to database insert
        accommodation_preferences: formData?.accommodationStandard,
        budget_range: budgetRange,
        additional_requirements: formData?.specialRequirements || formData?.moreDetails || null,
        metadata: metadata,
        status: 'proposed'
      };

      const { data: enquiry, error: enquiryError } = await supabase?.from('enquiries')?.insert([enquiryData])?.select()?.single();

      if (enquiryError) {
        console.error('Enquiry creation error:', enquiryError);
        throw new Error(enquiryError.message || 'Failed to create enquiry');
      }

      // Show success modal instead of navigating
      setSubmittedEnquiryId(enquiry?.id);
      setShowSuccessModal(true);

    } catch (err) {
      console.error('Submission error:', err);
      setError(err?.message || 'Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/customer-dashboard');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <TripTypeSelector
            selectedType={formData?.tripType}
            onTypeSelect={(type) => setFormData({ ...formData, tripType: type })}
            error={errors?.tripType}
          />
        );
      case 2:
        return (
          <DestinationSelector
            selectedDestinations={formData?.destination}
            onDestinationSelect={(destinations) => setFormData({ ...formData, destination: destinations })}
            error={errors?.destination}
          />
        );
      case 3:
        return (
          <DateSelector
            selectedDateType={formData?.dateType}
            selectedDate={formData?.selectedDate}
            onDateChange={(dateInfo) => setFormData({ 
              ...formData, 
              dateType: dateInfo?.type, 
              selectedDate: dateInfo 
            })}
            error={errors?.dateType}
          />
        );
      case 4:
        return (
          <div className="space-y-8">
            <BudgetSelector
              selectedBudget={formData?.budget}
              onBudgetChange={(budget) => setFormData({ ...formData, budget })}
              error={errors?.budget}
            />
            <AccommodationSelector
              selectedStandard={formData?.accommodationStandard}
              onStandardSelect={(standard) => setFormData({ ...formData, accommodationStandard: standard })}
              error={errors?.accommodation}
            />
          </div>
        );
      case 5:
        return (
          <AdditionalDetails
            adults={formData?.adults}
            children={formData?.children}
            onAdultsChange={(value) => setFormData({ ...formData, adults: value })}
            onChildrenChange={(value) => setFormData({ ...formData, children: value })}
            preferredNights={formData?.preferredNights}
            onPreferredNightsChange={(value) => setFormData({ ...formData, preferredNights: value })}
            specialRequirements={formData?.specialRequirements}
            onSpecialRequirementsChange={(requirements) => setFormData({ ...formData, specialRequirements: requirements })}
            moreDetails={formData?.moreDetails}
            onMoreDetailsChange={(details) => setFormData({ ...formData, moreDetails: details })}
            customerLocation={formData?.customerLocation}
            onCustomerLocationChange={(location) => setFormData({ ...formData, customerLocation: location })}
            travelPreferences={formData?.travelPreferences}
            onTravelPreferencesChange={(preferences) => setFormData({ ...formData, travelPreferences: preferences })}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header Navigation - Following HoliYay specifications */}
      <header className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={handleBackToHome}
              iconName="ArrowLeft"
              iconPosition="left"
              className="text-muted-foreground hover:text-foreground"
            >
              Back to Dashboard
            </Button>
            
            <div className="flex items-center space-x-2">
              <img
                src="/assets/images/No_Background-1763506485544.png"
                alt="HoliYay logo featuring a friendly fox mascot with Scottish heritage"
                className="h-8 w-auto"
              />
              <span className="font-semibold text-foreground">HoliYay</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Show loading state while profile loads */}
        {!userProfile && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm">Loading your profile...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Welcome Section with HoliYay Branding */}
        <div className="text-center space-y-6 mb-12">
          {/* Logo and Brand */}
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img
              src="/assets/images/No_Background-1763506485544.png"
              alt="HoliYay logo featuring a friendly fox mascot with Scottish heritage"
              className="h-16 w-auto"
            />
            <div>
              <h1 className="text-4xl font-bold text-foreground">Create Your Holiday Enquiry</h1>
              <p className="text-lg text-muted-foreground">Travel, simplified. Yay, amplified.</p>
            </div>
          </div>

          {/* Welcome Message */}
          <div className="space-y-3 max-w-2xl mx-auto">
            <p className="text-muted-foreground text-lg">
              Tell us about your dream holiday and we'll connect you with verified travel experts who specialize in creating unforgettable experiences.
            </p>
          </div>

          {/* Benefits Highlight */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="flex flex-col items-center space-y-3 p-6 bg-card rounded-lg border border-border">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
                <AppIcon name="Users" size={24} className="text-accent" />
              </div>
              <h3 className="font-semibold text-foreground">Expert Matching</h3>
              <p className="text-sm text-muted-foreground text-center">
                We'll match you with travel agents who specialize in your chosen destination and travel style
              </p>
            </div>
            
            <div className="flex flex-col items-center space-y-3 p-6 bg-card rounded-lg border border-border">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
                <AppIcon name="Heart" size={24} className="text-accent" />
              </div>
              <h3 className="font-semibold text-foreground">Personalized Proposals</h3>
              <p className="text-sm text-muted-foreground text-center">
                Receive tailored holiday proposals that match your budget, preferences, and travel dates
              </p>
            </div>
            
            <div className="flex flex-col items-center space-y-3 p-6 bg-card rounded-lg border border-border">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
                <AppIcon name="Shield" size={24} className="text-accent" />
              </div>
              <h3 className="font-semibold text-foreground">Trusted & Secure</h3>
              <p className="text-sm text-muted-foreground text-center">
                All our travel agents are verified professionals with proven track records of excellence
              </p>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="max-w-4xl mx-auto mb-8">
          <ProgressIndicator 
            currentStep={currentStep}
            totalSteps={totalSteps}
            steps={steps}
          />
        </div>

        {/* Form Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-2xl shadow-elevated border border-border p-8 lg:p-12">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {steps?.[currentStep - 1]?.title}
              </h2>
              <p className="text-muted-foreground">
                {steps?.[currentStep - 1]?.description}
              </p>
            </div>

            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-12 pt-8 border-t border-border">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                iconName="ChevronLeft"
                iconPosition="left"
                size="lg"
              >
                Previous
              </Button>

              <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                <span className="font-medium">Step {currentStep} of {totalSteps}</span>
                <div className="flex space-x-1">
                  {Array.from({ length: totalSteps }, (_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full ${
                        i + 1 <= currentStep ? 'bg-primary' : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <Button
                variant="default"
                onClick={handleNext}
                loading={isSubmitting}
                iconName={currentStep === totalSteps ? "Send" : "ChevronRight"}
                iconPosition="right"
                size="lg"
              >
                {currentStep === totalSteps ? 'Submit Enquiry' : 'Next Step'}
              </Button>
            </div>

            {error?.submit && (
              <div className="mt-6 p-4 bg-error/10 border border-error/20 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AppIcon name="AlertCircle" size={20} className="text-error" />
                  <span className="text-error">{error?.submit}</span>
                </div>
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-primary/5 rounded-2xl p-8 border border-primary/20">
            <div className="flex items-start space-x-4">
              <AppIcon name="HelpCircle" size={24} className="text-primary mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-3">Need Help Creating Your Enquiry?</h3>
                <p className="text-muted-foreground mb-4">
                  Our travel experts are here to help you create the perfect enquiry. The more details you provide, 
                  the better we can match you with the right travel agents who understand your vision.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="ghost" size="sm" iconName="MessageCircle">
                    Live Chat Support
                  </Button>
                  <Button variant="ghost" size="sm" iconName="Phone">
                    Call Us: 0800 123 4567
                  </Button>
                  <Button variant="ghost" size="sm" iconName="Mail">
                    Email: help@holiyay.co.uk
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Show submit button only when profile is loaded */}
        {currentStep === 6 && userProfile && (
          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentStep(5)}
              className="px-6 py-3 text-gray-700 hover:text-gray-900"
              disabled={isSubmitting}
            >
              ← Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !userProfile}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
            </button>
          </div>
        )}
      </main>

      {/* Success Modal */}
      <EnquirySuccessModal 
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        enquiryId={submittedEnquiryId}
      />
    </div>
  );
}